import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { MapPin, Radio, AlertTriangle, Search, Info, Check, X, ChevronRight, Zap, Settings2, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
});

const StatusBadge = ({ status }) => {
  const colors = {
    available: "bg-[#34C759]/20 text-[#34C759] border-[#34C759]/30",
    partially_occupied: "bg-[#FFCC00]/20 text-[#FFCC00] border-[#FFCC00]/30",
    occupied: "bg-[#FF3B30]/20 text-[#FF3B30] border-[#FF3B30]/30",
    restricted: "bg-[#FF3B30]/20 text-[#FF3B30] border-[#FF3B30]/30",
  };
  const labels = {
    available: "DISPONIBLE",
    partially_occupied: "PARCIAL",
    occupied: "OCUPADO",
    restricted: "RESTRINGIDO",
  };
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-sm border ${colors[status] || colors.available}`}>
      {labels[status] || status?.toUpperCase()}
    </span>
  );
};

export default function ChileFrequenciesPage({ user, onLogout }) {
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState("");
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [equipment, setEquipment] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState("generic");
  const [regionDetail, setRegionDetail] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [numChannels, setNumChannels] = useState(8);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetchRegions();
    fetchRecommendations();
    fetchEquipment();
  }, []);

  useEffect(() => {
    if (selectedRegion) {
      fetchRegionDetail(selectedRegion);
      fetchCities(selectedRegion);
      setSelectedCity("");
      setSearchResults(null);
    }
  }, [selectedRegion]);

  const fetchRegions = async () => {
    try {
      const response = await axios.get(`${API_BASE}/frequencies/chile/regions`, getAuthHeaders());
      setRegions(response.data.regions);
    } catch (error) {
      toast.error("Error al cargar regiones");
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async (regionId) => {
    try {
      const response = await axios.get(`${API_BASE}/frequencies/chile/cities/${regionId}`, getAuthHeaders());
      setCities(response.data.cities);
      // Auto-select capital as default
      if (response.data.capital) {
        setSelectedCity(response.data.capital);
      }
    } catch (error) {
      // Error handled silently
      setCities([]);
    }
  };

  const fetchEquipment = async () => {
    try {
      const response = await axios.get(`${API_BASE}/equipment`, getAuthHeaders());
      setEquipment(response.data.equipment);
    } catch (error) {
      // Error handled silently
    }
  };

  const fetchRegionDetail = async (regionId) => {
    try {
      const response = await axios.get(`${API_BASE}/frequencies/chile/regions/${regionId}`, getAuthHeaders());
      setRegionDetail(response.data);
    } catch (error) {
      toast.error("Error al cargar detalles de la región");
    }
  };

  const fetchRecommendations = async () => {
    try {
      const response = await axios.get(`${API_BASE}/frequencies/chile/recommendations`, getAuthHeaders());
      setRecommendations(response.data);
    } catch (error) {
      // Error handled silently
    }
  };

  const searchFrequencies = async () => {
    if (!selectedRegion) {
      toast.error("Selecciona una región primero");
      return;
    }
    setSearching(true);
    try {
      let url = `${API_BASE}/frequencies/chile/search?region_id=${selectedRegion}&num_channels=${numChannels}`;
      if (selectedEquipment && selectedEquipment !== "generic") {
        url += `&equipment_id=${selectedEquipment}`;
      }
      if (selectedCity) {
        url += `&city=${encodeURIComponent(selectedCity)}`;
      }
      
      const response = await axios.get(url, getAuthHeaders());
      setSearchResults(response.data);
      toast.success(`${response.data.suggestions.length} bandas encontradas para ${selectedCity || regionDetail?.name}`);
    } catch (error) {
      toast.error("Error al buscar frecuencias");
    } finally {
      setSearching(false);
    }
  };

  const getEquipmentLabel = (eqId) => {
    if (eqId === "generic") return "Scan Genérico";
    const eq = equipment.find(e => e.id === eqId);
    return eq ? eq.full_name : eqId;
  };

  const selectedEquipmentData = equipment.find(e => e.id === selectedEquipment);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="top-bar">
        <div>
          <h1 className="font-heading text-xl font-bold text-[#F4F4F5]">
            Frecuencias Chile
          </h1>
          <p className="text-sm text-[#71717A]">
            Base de datos de frecuencias disponibles por región y ciudad
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Selectors Panel */}
        <div className="lg:col-span-1 space-y-4">
          {/* Location Selection */}
          <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-5">
            <h2 className="font-heading font-semibold text-[#F4F4F5] mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#34C759]" />
              Ubicación
            </h2>
            
            <div className="space-y-4">
              {/* Region Selector */}
              <div>
                <label className="form-label">Región</label>
                <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                  <SelectTrigger className="input-tactical" data-testid="region-select">
                    <SelectValue placeholder="Selecciona una región" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10 max-h-[300px]">
                    {regions.map((region) => (
                      <SelectItem key={region.id} value={region.id}>
                        <div className="flex items-center gap-2">
                          <span className="text-[#71717A] text-xs w-8">{region.code}</span>
                          <span>{region.name.replace("Región de ", "").replace("Región del ", "")}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* City Selector */}
              {selectedRegion && cities.length > 0 && (
                <div>
                  <label className="form-label">Ciudad</label>
                  <Select value={selectedCity} onValueChange={setSelectedCity}>
                    <SelectTrigger className="input-tactical" data-testid="city-select">
                      <SelectValue placeholder="Selecciona una ciudad" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10 max-h-[250px]">
                      {cities.map((city) => (
                        <SelectItem key={city} value={city}>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-3 h-3 text-[#71717A]" />
                            {city}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {/* Equipment Selection */}
          <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-5">
            <h2 className="font-heading font-semibold text-[#F4F4F5] mb-4 flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-[#34C759]" />
              Equipo IEM
            </h2>
            
            <div className="space-y-4">
              <Select value={selectedEquipment} onValueChange={setSelectedEquipment}>
                <SelectTrigger className="input-tactical" data-testid="equipment-select">
                  <SelectValue placeholder="Selecciona un equipo" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="generic">
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-[#FFCC00]" />
                      <span>Scan Genérico</span>
                    </div>
                  </SelectItem>
                  {equipment.map((eq) => (
                    <SelectItem key={eq.id} value={eq.id}>
                      <div className="flex items-center gap-2">
                        <Radio className="w-4 h-4 text-[#34C759]" />
                        <span>{eq.full_name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedEquipmentData && (
                <div className="p-3 bg-[#0A0A0A] rounded-sm border border-white/[0.05] text-xs">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[#71717A]">Máx. Sistemas:</span>
                    <span className="text-[#34C759] font-mono">{selectedEquipmentData.max_systems}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#71717A]">Bandas:</span>
                    <span className="text-[#A1A1AA]">{selectedEquipmentData.frequency_bands.join(", ")}</span>
                  </div>
                </div>
              )}

              {selectedEquipment === "generic" && (
                <div className="p-3 bg-[#FFCC00]/10 rounded-sm border border-[#FFCC00]/30 text-xs">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-[#FFCC00] mt-0.5 flex-shrink-0" />
                    <p className="text-[#A1A1AA]">
                      El scan genérico usa espaciado estándar de 0.5 MHz, compatible con la mayoría de equipos IEM.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Channels & Search */}
          {selectedRegion && (
            <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-5">
              <h2 className="font-heading font-semibold text-[#F4F4F5] mb-4 flex items-center gap-2">
                <Radio className="w-5 h-5 text-[#34C759]" />
                Configuración
              </h2>
              
              <div className="space-y-4">
                <div className="form-group">
                  <div className="flex items-center justify-between mb-2">
                    <label className="form-label mb-0">Canales Necesarios</label>
                    <span className="font-mono text-[#34C759] text-lg">{numChannels}</span>
                  </div>
                  <Slider
                    value={[numChannels]}
                    onValueChange={(v) => setNumChannels(v[0])}
                    min={1}
                    max={selectedEquipmentData?.max_systems || 16}
                    step={1}
                    className="py-4"
                    data-testid="channels-slider"
                  />
                  <div className="flex justify-between text-xs text-[#71717A]">
                    <span>1</span>
                    <span>{selectedEquipmentData?.max_systems || 16}</span>
                  </div>
                </div>

                <Button
                  onClick={searchFrequencies}
                  disabled={searching || !selectedRegion}
                  className="btn-tactical btn-tactical-primary w-full"
                  data-testid="search-frequencies-button"
                >
                  {searching ? (
                    "Buscando..."
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      Buscar Frecuencias
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Recommendations based on selected equipment */}
          {recommendations && selectedEquipment !== "generic" && recommendations[selectedEquipment] && (
            <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-5">
              <h3 className="font-heading font-semibold text-[#F4F4F5] mb-4">
                Recomendaciones {getEquipmentLabel(selectedEquipment)}
              </h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-[#71717A] mb-1">Bandas Óptimas:</p>
                  <div className="flex flex-wrap gap-2">
                    {recommendations[selectedEquipment]?.optimal_bands?.map((band) => (
                      <span key={band} className="px-2 py-1 bg-[#34C759]/10 text-[#34C759] rounded-sm text-xs font-mono">
                        {band}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[#71717A] mb-1">Evitar:</p>
                  <div className="flex flex-wrap gap-2">
                    {recommendations[selectedEquipment]?.avoid_bands?.map((band) => (
                      <span key={band} className="px-2 py-1 bg-[#FF3B30]/10 text-[#FF3B30] rounded-sm text-xs">
                        {band}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results Panel */}
        <div className="lg:col-span-2">
          {!selectedRegion ? (
            <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-12 text-center">
              <MapPin className="w-16 h-16 mx-auto mb-4 text-[#71717A] opacity-30" />
              <h3 className="font-heading text-lg text-[#F4F4F5] mb-2">
                Selecciona una Ubicación
              </h3>
              <p className="text-[#71717A]">
                Elige una región y ciudad de Chile para ver las frecuencias disponibles
              </p>
            </div>
          ) : regionDetail ? (
            <div className="space-y-4">
              {/* Region Header */}
              <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-5">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 bg-[#34C759]/10 text-[#34C759] text-xs font-mono rounded">
                        {regionDetail.code}
                      </span>
                      <h2 className="font-heading text-lg font-semibold text-[#F4F4F5]">
                        {regionDetail.name}
                      </h2>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#71717A]">
                      <Building2 className="w-4 h-4" />
                      <span>
                        {selectedCity ? (
                          <span className="text-[#F4F4F5]">{selectedCity}</span>
                        ) : (
                          <span>Capital: {regionDetail.capital}</span>
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Radio className="w-5 h-5 text-[#34C759]" />
                    <span className="text-sm text-[#71717A]">
                      {regionDetail.iem_bands.length} bandas IEM
                    </span>
                  </div>
                </div>

                {/* Cities Tags */}
                <div className="mb-4">
                  <p className="text-xs text-[#71717A] mb-2">Ciudades en esta región:</p>
                  <div className="flex flex-wrap gap-1">
                    {regionDetail.cities.map((city) => (
                      <button
                        key={city}
                        onClick={() => setSelectedCity(city)}
                        className={`px-2 py-1 text-xs rounded-sm transition-colors ${
                          selectedCity === city
                            ? "bg-[#34C759] text-black font-medium"
                            : "bg-[#0A0A0A] text-[#A1A1AA] hover:bg-[#1A1A1A] border border-white/[0.05]"
                        }`}
                      >
                        {city}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Known Interferences */}
                {regionDetail.known_interferences.length > 0 && (
                  <div className="p-3 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-[#FF3B30]" />
                      <span className="font-medium text-[#FF3B30] text-sm">Interferencias Conocidas</span>
                    </div>
                    <ul className="space-y-1">
                      {regionDetail.known_interferences.map((int) => (
                        <li key={`${int.freq}-${int.source}`} className="text-xs text-[#A1A1AA] flex items-center gap-2">
                          <span className="font-mono text-[#FFCC00]">{int.freq} MHz</span>
                          <span>- {int.source}</span>
                          <span className={`px-1 rounded text-[10px] ${
                            int.level === "high" ? "bg-[#FF3B30]/20 text-[#FF3B30]" :
                            int.level === "medium" ? "bg-[#FFCC00]/20 text-[#FFCC00]" :
                            "bg-[#71717A]/20 text-[#71717A]"
                          }`}>
                            {int.level.toUpperCase()}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* IEM Bands Table */}
              <div className="bg-[#111111] border border-white/[0.08] rounded-sm overflow-hidden">
                <div className="p-4 border-b border-white/[0.08]">
                  <h3 className="font-heading font-semibold text-[#F4F4F5]">
                    Bandas Disponibles para Sistemas In-Ear
                  </h3>
                </div>
                <table className="data-table" data-testid="iem-bands-table">
                  <thead>
                    <tr>
                      <th>Banda</th>
                      <th>Rango (MHz)</th>
                      <th>Estado</th>
                      <th>Notas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {regionDetail.iem_bands.map((band) => (
                      <tr key={band.name} className={band.status === "restricted" ? "opacity-50" : ""}>
                        <td className="font-medium text-[#F4F4F5]">{band.name}</td>
                        <td>
                          <span className="font-mono text-[#34C759]">
                            {band.freq_min} - {band.freq_max}
                          </span>
                        </td>
                        <td>
                          <StatusBadge status={band.status} />
                        </td>
                        <td className="text-[#71717A] text-sm">{band.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Search Results */}
              {searchResults && (
                <div className="bg-[#111111] border border-[#34C759]/30 rounded-sm overflow-hidden">
                  <div className="p-4 border-b border-white/[0.08] bg-[#34C759]/5">
                    <div className="flex items-center justify-between">
                      <h3 className="font-heading font-semibold text-[#34C759]">
                        Frecuencias para {searchResults.city || searchResults.region}
                      </h3>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#71717A]">Equipo:</span>
                        <span className="px-2 py-1 bg-[#0A0A0A] text-[#F4F4F5] text-xs rounded-sm">
                          {searchResults.equipment}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-[#71717A] mt-1">
                      {searchResults.channels_requested} canales • Espaciado mínimo: {searchResults.min_spacing_used} MHz
                    </p>
                  </div>
                  
                  <div className="p-4 space-y-4">
                    {searchResults.suggestions.length === 0 ? (
                      <div className="text-center py-8">
                        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-[#FFCC00] opacity-50" />
                        <p className="text-[#A1A1AA]">No se encontraron bandas con suficiente espacio para {searchResults.channels_requested} canales</p>
                        <p className="text-xs text-[#71717A] mt-2">Intenta reducir el número de canales</p>
                      </div>
                    ) : (
                      searchResults.suggestions.map((suggestion) => (
                        <div 
                          key={suggestion.band_name} 
                          className={`p-4 bg-[#0A0A0A] border rounded-sm ${
                            suggestion.is_optimal_for_equipment 
                              ? "border-[#34C759]/50" 
                              : "border-white/[0.08]"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-[#F4F4F5]">{suggestion.band_name}</span>
                              {suggestion.is_optimal_for_equipment && (
                                <span className="px-2 py-0.5 bg-[#34C759]/10 text-[#34C759] text-[10px] rounded-sm">
                                  ÓPTIMA
                                </span>
                              )}
                              <StatusBadge status={suggestion.status} />
                            </div>
                            <span className="text-xs text-[#71717A] font-mono">{suggestion.freq_range}</span>
                          </div>
                          
                          <div className="flex flex-wrap gap-2 mb-3">
                            {suggestion.suggested_frequencies.map((freq, j) => (
                              <div key={j} className="px-3 py-2 bg-[#111111] border border-[#34C759]/30 rounded-sm">
                                <span className="text-xs text-[#71717A]">CH {j + 1}</span>
                                <p className="font-mono text-[#34C759]">{freq.toFixed(3)}</p>
                              </div>
                            ))}
                          </div>
                          
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-[#71717A]">{suggestion.notes}</span>
                            <span className="font-mono text-[#A1A1AA]">
                              Espaciado: {suggestion.spacing} MHz
                            </span>
                          </div>
                        </div>
                      ))
                    )}

                    {/* Warnings */}
                    {searchResults.warnings.length > 0 && (
                      <div className="p-3 bg-[#FFCC00]/10 border border-[#FFCC00]/30 rounded-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <AlertTriangle className="w-4 h-4 text-[#FFCC00]" />
                          <span className="text-sm font-medium text-[#FFCC00]">Advertencias</span>
                        </div>
                        <ul className="text-xs text-[#A1A1AA] space-y-1">
                          {searchResults.warnings.map((w) => (
                            <li key={`${w.freq}-${w.source}`}>
                              Evitar <span className="font-mono text-[#FFCC00]">{w.freq} MHz</span> - {w.source}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Avoid Ranges */}
                    {searchResults.avoid_ranges && searchResults.avoid_ranges.length > 0 && (
                      <div className="p-3 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <X className="w-4 h-4 text-[#FF3B30]" />
                          <span className="text-sm font-medium text-[#FF3B30]">Rangos a Evitar</span>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {searchResults.avoid_ranges.map((range) => (
                            <span key={range} className="px-2 py-1 bg-[#FF3B30]/5 text-[#FF3B30] text-xs font-mono rounded-sm">
                              {range}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-64">
              <p className="text-[#71717A]">Cargando...</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
